import { forwardRef, useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchFloorViewSvg } from '@apis/storage';
import { sanitizeSvg } from '@utils/sanitizeSvg';
import { areaHighlightSelector } from '@utils/svgMarkers';
import styles from './styles.module.css';

interface IFloorSvgProps {
  siteId: string;
  floorId: string;
  /**
   * When set, every area-outline node matching `areaHighlightSelector` (bare
   * `area<id>`, floor-prefixed `f<n>area<id>`, and Illustrator `_<n>` dedupe
   * suffixes, case-insensitive) receives `data-highlight="true"` (paired CSS in
   * `styles.module.css`). Cleared on undefined or change; no-ops when nothing matches.
   */
  highlightAreaId?: string;
  className?: string;
}

/**
 * Reusable floor-plan primitive: fetches the per-floor SVG from the
 * storage gateway and inlines it into a div so callers can layer on
 * positioned overlays. Optional `highlightAreaId` flags the matching
 * area outline via `data-highlight="true"` (paired CSS in
 * `styles.module.css`).
 *
 * The container div is exposed via `ref` (forwardRef) so callers like
 * `MonitorPage/MainView/FloorDrawer` can read the same DOM tree to
 * tighten the SVG viewBox or measure anchor positions. While loading or
 * on error, the same div is rendered (empty + role="status"/alert) so
 * callers' refs stay stable across state transitions.
 */
export const FloorSvg = forwardRef<HTMLDivElement, IFloorSvgProps>(function FloorSvg(
  { siteId, floorId, highlightAreaId, className },
  externalRef,
) {
  const innerRef = useRef<HTMLDivElement>(null);
  // Combine inner + forwarded refs onto the same element. Using a callback
  // ref so both refs update every time React (re)attaches the node.
  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      innerRef.current = node;
      if (typeof externalRef === 'function') externalRef(node);
      else if (externalRef) externalRef.current = node;
    },
    [externalRef],
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: ['storage', 'floor-view', siteId, floorId],
    queryFn: ({ signal }) => fetchFloorViewSvg(siteId, floorId, signal),
    enabled: Boolean(siteId && floorId),
    staleTime: 5 * 60_000,
  });

  // Inject SVG markup imperatively so we can mutate area nodes for highlight.
  useEffect(() => {
    const root = innerRef.current;
    if (!root) return;
    if (!data) {
      // Clear stale markup if upstream returned null (404 → no plan) or
      // we're still loading. Avoids leaking the previous floor's SVG
      // when (siteId, floorId) changes.
      root.innerHTML = '';
      return;
    }
    // Sanitise upstream SVG before inlining — see `sanitizeSvg` threat
    // model. Backend storage proxy serves admin-uploaded content.
    root.innerHTML = sanitizeSvg(data);
  }, [data]);

  // Toggle data-highlight on the matching area node. Clears any prior
  // highlight first so the prop's value is the source of truth.
  useEffect(() => {
    const root = innerRef.current;
    if (!root) return;
    root
      .querySelectorAll('[data-highlight="true"]')
      .forEach((n) => n.removeAttribute('data-highlight'));
    if (!highlightAreaId) return;
    // `areaHighlightSelector` matches bare (`area17C`) and floor-prefixed
    // (`f17area17C`) outline ids, with/without Illustrator's `_<n>` dedupe suffix,
    // case-insensitively — so highlighting works across every project's SVGs, not
    // just the exact-id ones.
    root
      .querySelectorAll(areaHighlightSelector(highlightAreaId))
      .forEach((n) => n.setAttribute('data-highlight', 'true'));
  }, [highlightAreaId, data]);

  const stateRole = isLoading ? 'status' : isError ? 'alert' : undefined;
  const stateClass = !data && (isLoading || isError) ? ` ${styles.state}` : '';
  const cls = `${styles.svg}${stateClass} ${className ?? ''}`.trim();
  return (
    <div
      ref={setRef}
      className={cls}
      role={stateRole}
      aria-live={isLoading ? 'polite' : undefined}
    />
  );
});
